import React, { useRef, useState } from 'react';
import { fetchWithAuth, getAuthHeaders } from '../../utils/auth';
import './ShpImporter.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MODEL_TYPES = [
  { key: 'region',    label: 'Région'    },
  { key: 'district',  label: 'District'  },
  { key: 'commune',   label: 'Commune'   },
  { key: 'fokontany', label: 'Fokontany' },
];

const MODEL_FIELDS = {
  region:    [
    { key: 'nom',  label: 'Nom',  required: true  },
    { key: 'code', label: 'Code', required: false  },
  ],
  district:  [
    { key: 'nom',        label: 'Nom',          required: true  },
    { key: 'code',       label: 'Code',         required: false },
    { key: 'region_nom', label: 'Région (nom)', required: false },
  ],
  commune:   [
    { key: 'nom',          label: 'Nom',              required: true  },
    { key: 'code',         label: 'Code',             required: false },
    { key: 'district_nom', label: 'District (nom)',   required: false },
  ],
  fokontany: [
    { key: 'nom',         label: 'Nom',             required: true  },
    { key: 'code',        label: 'Code',            required: false },
    { key: 'commune_nom', label: 'Commune (nom)',   required: false },
  ],
};

const STEP = { UPLOAD: 1, MAPPING: 2, RESULT: 3 };

export default function ShpImporter({ onClose }) {
  const fileRef = useRef(null);

  const [step,       setStep]       = useState(STEP.UPLOAD);
  const [modelType,  setModelType]  = useState('region');
  const [file,       setFile]       = useState(null);
  const [dragging,   setDragging]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  // Données preview
  const [preview,    setPreview]    = useState(null); // { fields, sample_rows, feature_count, geometry_type }

  // Mapping : { model_field_key → shp_column_name | '' }
  const [mapping,    setMapping]    = useState({});

  // Résultat import
  const [result,     setResult]     = useState(null);

  // ── Drag & Drop ──
  const onDragOver  = e => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onDrop      = e => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) pickFile(f);
  };

  const pickFile = f => {
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['zip', 'shp'].includes(ext)) {
      setError('Formats acceptés : .zip ou .shp');
      return;
    }
    setFile(f);
    setError('');
  };

  // ── Étape 1 → 2 : envoyer preview ──
  const handlePreview = async () => {
    if (!file) { setError('Choisissez un fichier.'); return; }
    setLoading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('model_type', modelType);

      const res  = await fetchWithAuth(`${BASE_URL}/api/v1/sig/preview/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Erreur ${res.status}`);

      // Mapping initial : on cherche automatiquement les correspondances évidentes
      const fields    = MODEL_FIELDS[modelType];
      const shpFields = data.fields.map(f => f.name);
      const initMap   = {};
      fields.forEach(mf => {
        // Chercher une colonne SHP dont le nom ressemble au champ modèle
        const match = shpFields.find(sf =>
          sf.toLowerCase() === mf.key.toLowerCase() ||
          sf.toLowerCase().includes(mf.key.split('_')[0].toLowerCase())
        );
        initMap[mf.key] = match || '';
      });

      setPreview(data);
      setMapping(initMap);
      setStep(STEP.MAPPING);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Étape 2 → 3 : importer ──
  const handleImport = async () => {
    const required = MODEL_FIELDS[modelType].filter(f => f.required);
    for (const rf of required) {
      if (!mapping[rf.key]) {
        setError(`Le champ "${rf.label}" est obligatoire — assignez une colonne.`);
        return;
      }
    }

    setLoading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('model_type', modelType);
      fd.append('mapping', JSON.stringify(mapping));

      const res  = await fetchWithAuth(`${BASE_URL}/api/v1/sig/import/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Erreur ${res.status}`);

      setResult(data);
      setStep(STEP.RESULT);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(STEP.UPLOAD);
    setFile(null);
    setPreview(null);
    setMapping({});
    setResult(null);
    setError('');
  };

  const shpColumns = preview ? ['', ...preview.fields.map(f => f.name)] : [];

  return (
    <div className="shi-overlay" onClick={e => e.target === e.currentTarget && onClose?.()}>
      <div className="shi-modal">

        {/* ── Header ── */}
        <div className="shi-header">
          <div className="shi-title">
            <span className="shi-title-icon">⬆</span>
            Import Shapefile — Limites administratives
          </div>
          <button className="shi-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Stepper ── */}
        <div className="shi-stepper">
          {['Fichier', 'Correspondances', 'Résultat'].map((label, idx) => {
            const n = idx + 1;
            return (
              <React.Fragment key={n}>
                <div className={`shi-step ${step >= n ? 'active' : ''} ${step > n ? 'done' : ''}`}>
                  <div className="shi-step-circle">{step > n ? '✓' : n}</div>
                  <span className="shi-step-label">{label}</span>
                </div>
                {idx < 2 && <div className={`shi-step-line ${step > n ? 'done' : ''}`} />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="shi-body">
          {error && <div className="shi-error">{error}</div>}

          {/* ══ ÉTAPE 1 : UPLOAD ══ */}
          {step === STEP.UPLOAD && (
            <div className="shi-upload-step">
              <div className="shi-field">
                <label className="shi-label">Type de limite</label>
                <div className="shi-type-grid">
                  {MODEL_TYPES.map(t => (
                    <button
                      key={t.key}
                      className={`shi-type-btn ${modelType === t.key ? 'active' : ''}`}
                      onClick={() => setModelType(t.key)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="shi-field">
                <label className="shi-label">Fichier shapefile</label>
                <div
                  className={`shi-dropzone ${dragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  onClick={() => fileRef.current?.click()}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".zip,.shp"
                    style={{ display: 'none' }}
                    onChange={e => e.target.files[0] && pickFile(e.target.files[0])}
                  />
                  {file ? (
                    <>
                      <div className="shi-file-icon">📄</div>
                      <div className="shi-file-name">{file.name}</div>
                      <div className="shi-file-size">{(file.size / 1024).toFixed(1)} Ko</div>
                    </>
                  ) : (
                    <>
                      <div className="shi-drop-icon">📂</div>
                      <div className="shi-drop-text">Glissez un fichier <strong>.zip</strong> ou <strong>.shp</strong></div>
                      <div className="shi-drop-hint">ou cliquez pour parcourir</div>
                    </>
                  )}
                </div>
              </div>

              <div className="shi-note">
                Le ZIP doit contenir les fichiers <code>.shp</code>, <code>.dbf</code> et <code>.shx</code>. La projection sera reprojetée automatiquement en WGS84.
              </div>

              <div className="shi-actions">
                <button className="shi-btn shi-btn--ghost" onClick={onClose}>Annuler</button>
                <button
                  className="shi-btn shi-btn--primary"
                  onClick={handlePreview}
                  disabled={!file || loading}
                >
                  {loading ? 'Lecture…' : 'Analyser le fichier →'}
                </button>
              </div>
            </div>
          )}

          {/* ══ ÉTAPE 2 : MAPPING ══ */}
          {step === STEP.MAPPING && preview && (
            <div className="shi-mapping-step">
              <div className="shi-preview-info">
                <span className="shi-badge">{preview.feature_count} entités</span>
                <span className="shi-badge shi-badge--geo">{preview.geometry_type}</span>
                <span className="shi-badge shi-badge--type">{MODEL_TYPES.find(t => t.key === modelType)?.label}</span>
              </div>

              {/* Tableau aperçu */}
              <div className="shi-preview-wrap">
                <div className="shi-preview-title">Aperçu des données (5 premières lignes)</div>
                <div className="shi-table-scroll">
                  <table className="shi-table">
                    <thead>
                      <tr>
                        {preview.fields.map(f => (
                          <th key={f.name}><span>{f.name}</span><em>{f.type}</em></th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample_rows.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j}>{cell == null ? <em>null</em> : String(cell)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mapping */}
              <div className="shi-mapping-title">Correspondance des champs</div>
              <div className="shi-mapping-grid">
                <div className="shi-mapping-col-hdr">Champ du modèle</div>
                <div className="shi-mapping-col-hdr">Colonne du shapefile</div>
                <div className="shi-mapping-col-hdr">Exemple</div>

                {MODEL_FIELDS[modelType].map(mf => {
                  const selectedCol = mapping[mf.key] || '';
                  const colIdx      = preview.fields.findIndex(f => f.name === selectedCol);
                  const example     = colIdx >= 0 && preview.sample_rows[0]
                    ? String(preview.sample_rows[0][colIdx] ?? '')
                    : '—';
                  return (
                    <React.Fragment key={mf.key}>
                      <div className="shi-mapping-field">
                        {mf.label}
                        {mf.required && <span className="shi-req">*</span>}
                      </div>
                      <div>
                        <select
                          className="shi-select"
                          value={selectedCol}
                          onChange={e => setMapping(m => ({ ...m, [mf.key]: e.target.value }))}
                        >
                          <option value="">— ignorer —</option>
                          {preview.fields.map(f => (
                            <option key={f.name} value={f.name}>{f.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="shi-example">{example}</div>
                    </React.Fragment>
                  );
                })}
              </div>

              <div className="shi-note shi-note--info">
                Les colonnes non assignées seront enregistrées dans les <strong>attributs supplémentaires</strong> (meta) de chaque entité.
              </div>

              <div className="shi-actions">
                <button className="shi-btn shi-btn--ghost" onClick={() => setStep(STEP.UPLOAD)}>← Retour</button>
                <button
                  className="shi-btn shi-btn--primary"
                  onClick={handleImport}
                  disabled={loading}
                >
                  {loading ? 'Import en cours…' : `Importer ${preview.feature_count} entités`}
                </button>
              </div>
            </div>
          )}

          {/* ══ ÉTAPE 3 : RÉSULTAT ══ */}
          {step === STEP.RESULT && result && (
            <div className="shi-result-step">
              <div className="shi-result-icon">{result.imported > 0 ? '✅' : '⚠️'}</div>
              <div className="shi-result-title">
                {result.imported} entité{result.imported > 1 ? 's' : ''} importée{result.imported > 1 ? 's' : ''}
                {result.total > result.imported && ` (${result.total - result.imported} doublon(s) ignoré(s))`}
              </div>
              <div className="shi-result-type">
                Type : <strong>{MODEL_TYPES.find(t => t.key === modelType)?.label}</strong>
              </div>

              {result.errors?.length > 0 && (
                <details className="shi-errors-detail">
                  <summary>{result.errors.length} avertissement(s)</summary>
                  <ul className="shi-errors-list">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </details>
              )}

              <div className="shi-actions">
                <button className="shi-btn shi-btn--ghost" onClick={reset}>Importer un autre fichier</button>
                <button className="shi-btn shi-btn--primary" onClick={onClose}>Fermer</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
